import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${window.__BACKEND__}/api`;
const TOKEN_KEY = "vdh_user_token";

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const fetchMe = useCallback(async (tkn) => {
    try {
      const r = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${tkn}` },
      });
      setUser(r.data);
    } catch (err) {
      // Only log out when the token is actually rejected (401/403).
      // On network blips / 5xx keep the session so the user isn't bounced to login.
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setUser(null);
      }
    } finally {
      setChecking(false);
    }
  }, []);

  // Keep axios default Authorization header in sync with current token
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchMe(token);
    } else {
      setChecking(false);
    }
  }, [token, fetchMe]);

  const login = async (email, password, remember = true) => {
    const r = await axios.post(`${API}/auth/login`, { email, password, remember });
    localStorage.setItem(TOKEN_KEY, r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const register = async (name, email, password) => {
    const r = await axios.post(`${API}/auth/register`, { name, email, password });
    return r.data;
  };

  const setSession = (newToken, newUser) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    if (newUser) setUser(newUser);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setUser(null);
  };

  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  return (
    <AuthContext.Provider
      value={{ token, user, checking, login, register, logout, setSession, authHeader, refresh: () => token && fetchMe(token) }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return err?.message || "Errore imprevisto";
}
